import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const InventorySync = ({ tenantId, onStockUpdate }) => {
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, synced, error
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    if (!tenantId) return;

    // Setup real-time subscription for inventory changes
    const channel = supabase
      ?.channel('inventory-sync-channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pos_products',
          filter: `tenant_id=eq.${tenantId}`
        },
        (payload) => {
          handleInventoryChange(payload);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'pos_inventory_logs',
          filter: `tenant_id=eq.${tenantId}`
        },
        (payload) => {
          handleInventoryLogChange(payload);
        }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setSyncStatus('synced');
          setLastSyncTime(new Date());
        } else if (status === 'CHANNEL_ERROR') {
          setSyncStatus('error');
        }
      });

    setRealtimeChannel(channel);

    // Cleanup subscription on unmount
    return () => {
      if (channel) {
        supabase?.removeChannel(channel);
      }
    };
  }, [tenantId]);

  const handleInventoryChange = (payload) => {
    setSyncStatus('syncing');
    
    // Notify parent component of stock update
    if (payload?.eventType === 'UPDATE' || payload?.eventType === 'INSERT') {
      onStockUpdate?.(payload?.new);
      setLastSyncTime(new Date());
    } else if (payload?.eventType === 'DELETE') {
      onStockUpdate?.(payload?.old, 'delete');
      setLastSyncTime(new Date());
    }
    
    // Reset to synced after brief delay
    setTimeout(() => {
      setSyncStatus('synced');
    }, 500);
  };

  const handleInventoryLogChange = (payload) => {
    // Handle inventory log changes (restocks, adjustments, etc.)
    if (payload?.new) {
      setLastSyncTime(new Date());
    }
  };

  const getSyncStatusColor = () => {
    switch (syncStatus) {
      case 'synced':
        return 'text-success';
      case 'syncing':
        return 'text-primary';
      case 'error':
        return 'text-danger';
      default:
        return 'text-muted-foreground';
    }
  };

  const getSyncStatusIcon = () => {
    switch (syncStatus) {
      case 'synced':
        return 'CheckCircle';
      case 'syncing':
        return 'RefreshCw';
      case 'error':
        return 'AlertCircle';
      default:
        return 'Radio';
    }
  };

  const getSyncStatusText = () => {
    switch (syncStatus) {
      case 'synced':
        return 'Synced';
      case 'syncing':
        return 'Syncing...';
      case 'error':
        return 'Sync Error';
      default:
        return 'Connecting...';
    }
  };

  const formatLastSyncTime = () => {
    if (!lastSyncTime) return 'Never';
    
    const now = new Date();
    const diff = Math.floor((now - lastSyncTime) / 1000); // seconds
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return lastSyncTime?.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg">
      <Icon
        name={getSyncStatusIcon()}
        size={16}
        className={`${getSyncStatusColor()} ${syncStatus === 'syncing' ? 'animate-spin' : ''}`}
      />
      <div className="flex flex-col">
        <span className={`text-xs font-medium ${getSyncStatusColor()}`}>
          {getSyncStatusText()}
        </span>
        <span className="text-xs text-muted-foreground">
          {formatLastSyncTime()}
        </span>
      </div>
    </div>
  );
};

export default InventorySync;